import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { fetchProducts } from "../api";
import { ProductCard } from "./ProductCard";

export const SearchOverlay = ({ open, onClose, initialQuery = "" }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) setQ(initialQuery);
  }, [open, initialQuery]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    if (window.__lenis) open ? window.__lenis.stop() : window.__lenis.start();
    if (open) setTimeout(() => inputRef.current?.focus(), 350);
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => {
      document.body.style.overflow = "";
      window.__lenis?.start();
      window.removeEventListener("keydown", esc);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      fetchProducts(q.trim() ? { q: q.trim() } : {}).then(setResults).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="search-overlay"
          className="fixed inset-0 z-50 bg-white flex flex-col"
          initial={{ y: "-100%" }}
          animate={{ y: 0 }}
          exit={{ y: "-100%" }}
          transition={{ duration: 0.5, ease: [0.76, 0, 0.24, 1] }}
        >
          <div className="flex items-center justify-between h-14 md:h-16 px-4 md:px-8 flex-shrink-0">
            <p className="text-[11px] font-medium tracking-[0.18em]">SEARCH</p>
            <button
              data-testid="search-close-button"
              onClick={onClose}
              aria-label="Close search"
              className="hover:rotate-90 transition-transform duration-300"
            >
              <X className="w-6 h-6" strokeWidth={1.5} />
            </button>
          </div>
          <div className="px-4 md:px-8 pt-6 md:pt-12">
            <input
              ref={inputRef}
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="SEARCH PRODUCTS…"
              className="w-full bg-transparent border-b border-black pb-4 text-2xl md:text-4xl font-medium tracking-wide uppercase placeholder:text-black/25 focus:outline-none"
            />
          </div>
          <div className="flex-1 overflow-y-auto px-4 md:px-8 py-8">
            {results && q.trim() && results.length === 0 && (
              <p className="text-[11px] tracking-[0.18em] text-black/50" data-testid="search-no-results">
                NO RESULTS FOR “{q.toUpperCase()}”
              </p>
            )}
            {results && results.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-8" data-testid="search-results">
                {results.slice(0, 12).map((p) => (
                  <div key={p.id} onClick={onClose}>
                    <ProductCard product={p} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
